import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Edit3, BookOpen } from 'lucide-react';
import { Button, FAB, EmptyState } from '../../components/ui';
import { PageHeader } from '../../components/ui/PageHeader';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { materialsApi } from '../../api/materials';
import { CATEGORY_CONFIG } from '../../types/material';
import type { Material, MaterialCategory } from '../../types/material';
import { useTabNavigationStore } from '../../store/tabNavigationStore';

interface Props {
  assistantId: string;
}

const MaterialsScreen: React.FC<Props> = ({ assistantId }) => {
  const { popScreen } = useTabNavigationStore();
  const queryClient = useQueryClient();

  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [viewingMaterial, setViewingMaterial] = useState<Material | null>(null);

  const { data: materials, isLoading } = useQuery({
    queryKey: ['materials', assistantId],
    queryFn: () => materialsApi.getByAssistantId(assistantId),
    enabled: !!assistantId,
  });

  const createMutation = useMutation({
    mutationFn: (data: { title: string; content: string; category: MaterialCategory; tags: string[] }) =>
      materialsApi.create(assistantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials', assistantId] });
      setShowCreateSheet(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { title: string; content: string; category: MaterialCategory; tags: string[] } }) =>
      materialsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials', assistantId] });
      setEditingMaterial(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => materialsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials', assistantId] });
      setViewingMaterial(null);
    },
  });

  // Group materials by category
  const grouped = (materials || []).reduce<Record<string, Material[]>>((acc, m) => {
    if (!acc[m.category]) acc[m.category] = [];
    acc[m.category].push(m);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="animate-pulse" style={{ color: 'var(--text-secondary)' }}>Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <PageHeader
        title="Материалы"
        showBackButton
        onBackClick={popScreen}
      />

      <div className="px-4 pt-4">
        {!materials || materials.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="Нет материалов"
            description="Добавьте учебные материалы: требования к аттестации, нормативы, правила"
            primaryAction={{
              label: 'Добавить материал',
              onClick: () => setShowCreateSheet(true),
            }}
          />
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([cat, items]) => {
              const config = CATEGORY_CONFIG[cat as MaterialCategory] || CATEGORY_CONFIG.general;
              return (
                <div key={cat}>
                  <h3 className="text-[13px] font-semibold uppercase tracking-wide mb-2 px-1" style={{ color: 'var(--text-secondary)' }}>
                    {config.icon} {config.label}
                  </h3>
                  <div className="space-y-2">
                    {items.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setViewingMaterial(m)}
                        className="w-full text-left rounded-xl p-4 transition-opacity active:opacity-70"
                        style={{ backgroundColor: 'var(--bg-secondary)' }}
                      >
                        <div className="text-[17px] font-medium" style={{ color: 'var(--text-primary)' }}>
                          {m.title}
                        </div>
                        {m.content && (
                          <div className="text-[13px] mt-1 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                            {m.content.slice(0, 120)}{m.content.length > 120 ? '...' : ''}
                          </div>
                        )}
                        {m.tags && m.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {m.tags.map((tag) => (
                              <span
                                key={tag}
                                className="text-[11px] px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {materials && materials.length > 0 && (
        <FAB
          onClick={() => setShowCreateSheet(true)}
          icon={<Plus size={28} color="#FFFFFF" strokeWidth={2.5} />}
        />
      )}

      {/* Create Sheet */}
      <MaterialFormSheet
        isOpen={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        isSubmitting={createMutation.isPending}
        title="Добавить материал"
      />

      {/* Edit Sheet */}
      <MaterialFormSheet
        isOpen={!!editingMaterial}
        onClose={() => setEditingMaterial(null)}
        onSubmit={(data) => editingMaterial && updateMutation.mutate({ id: editingMaterial.id, data })}
        isSubmitting={updateMutation.isPending}
        title="Редактировать"
        initial={editingMaterial || undefined}
      />

      {/* View Sheet */}
      <BottomSheet
        isOpen={!!viewingMaterial}
        onClose={() => setViewingMaterial(null)}
        title={viewingMaterial?.title || ''}
        height="90%"
      >
        {viewingMaterial && (
          <div className="pt-4 px-1">
            <div className="flex gap-2 mb-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingMaterial(viewingMaterial);
                  setViewingMaterial(null);
                }}
                className="flex-1 !h-9 !text-[13px]"
              >
                <Edit3 size={14} className="mr-1" />
                Редактировать
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  if (confirm('Удалить этот материал?')) {
                    deleteMutation.mutate(viewingMaterial.id);
                  }
                }}
                className="!h-9 !text-[13px] !px-3"
              >
                <Trash2 size={14} />
              </Button>
            </div>

            {viewingMaterial.tags && viewingMaterial.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {viewingMaterial.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[12px] px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'var(--accent-muted)', color: 'var(--accent)' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div
              className="text-[15px] leading-relaxed whitespace-pre-wrap"
              style={{ color: 'var(--text-primary)' }}
            >
              {viewingMaterial.content}
            </div>
          </div>
        )}
      </BottomSheet>
    </div>
  );
};

// -- Form Sheet Component --

interface FormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; content: string; category: MaterialCategory; tags: string[] }) => void;
  isSubmitting: boolean;
  title: string;
  initial?: Material;
}

const MaterialFormSheet: React.FC<FormSheetProps> = ({ isOpen, onClose, onSubmit, isSubmitting, title, initial }) => {
  const [formTitle, setFormTitle] = useState(initial?.title || '');
  const [content, setContent] = useState(initial?.content || '');
  const [category, setCategory] = useState<MaterialCategory>(initial?.category || 'requirements');
  const [tagsStr, setTagsStr] = useState(initial?.tags?.join(', ') || '');

  // Sync form state when initial changes (e.g. opening edit for a different material)
  useEffect(() => {
    setFormTitle(initial?.title || '');
    setContent(initial?.content || '');
    setCategory(initial?.category || 'requirements');
    setTagsStr(initial?.tags?.join(', ') || '');
  }, [initial?.id]);

  const resetKey = initial?.id || 'new';

  const handleSubmit = () => {
    if (!formTitle.trim()) return;
    const tags = tagsStr.split(',').map((t) => t.trim()).filter(Boolean);
    onSubmit({ title: formTitle.trim(), content, category, tags });
    if (!initial) {
      setFormTitle('');
      setContent('');
      setCategory('requirements');
      setTagsStr('');
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title} height="90%" key={resetKey}>
      <div className="space-y-4 pt-4">
        <div>
          <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Заголовок *
          </label>
          <input
            type="text"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="Название материала"
            disabled={isSubmitting}
            className="w-full h-11 px-3 rounded-xl border-none outline-none text-[17px]"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          />
        </div>

        <div>
          <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Категория
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as MaterialCategory)}
            disabled={isSubmitting}
            className="w-full h-11 px-3 rounded-xl border-none outline-none text-[17px] appearance-none"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          >
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Содержание
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Опишите требования, нормативы или правила..."
            disabled={isSubmitting}
            rows={10}
            className="w-full px-3 py-2 rounded-xl border-none outline-none text-[15px] resize-none"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          />
        </div>

        <div>
          <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Теги (через запятую)
          </label>
          <input
            type="text"
            value={tagsStr}
            onChange={(e) => setTagsStr(e.target.value)}
            placeholder="уровень, разряд, норматив"
            disabled={isSubmitting}
            className="w-full h-11 px-3 rounded-xl border-none outline-none text-[15px]"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          />
        </div>

        <div className="pt-2 pb-4">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!formTitle.trim() || isSubmitting}
            className="w-full !h-12"
          >
            {isSubmitting ? 'Сохранение...' : initial ? 'Сохранить' : 'Создать'}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default MaterialsScreen;
